import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    MenuItem,
    MenuList,
    Popover,
    Typography
} from '@mui/material';
import { usePostHog } from '@posthog/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { capture } from '../../analytics/posthog';
import ApiClient from '../../config/ApiClient';
import { useAppContext } from '../../context/AppContext';
import { useLessonPlanEditor } from '../../context/LessonPlanEditorContext';
import { useLessonPlanRecording } from '../../context/LessonPlanRecordingContext';
import { LessonPlanRole, LessonPlanSessionModel, SessionSlideModel } from '../../object-actions/types/types';
import LessonPlanFacilitatorSession from './LessonPlanFacilitatorSession';
import { areAllSlidesTrackedForSession, canManageSessionProgress } from './lessonPlanProgressUtils';
import LessonPlanScriptedSession from './LessonPlanScriptedSession';
import LessonPlanSlide from './LessonPlanSlide';
import { VoiceNavigationCommand, VoiceNavigationIntent } from './voiceNavigation';

type FacilitatorMode = 'conversational' | 'scripted';


interface LessonPlanSlideDeckProps {
    session: LessonPlanSessionModel;
    lessonPlanId?: number | null;
    isExpanded?: boolean; // Only track progress when expanded
    myRoles?: LessonPlanRole[];
    isPublished?: boolean;
    hasHumanCoach?: boolean;
    /** When provided, called after a successful update_slide_progress to update local state so Mark Complete becomes visible without refetch. */
    updateSlide?: (slideId: number, updates: Partial<SessionSlideModel>) => void;
    updateSession?: (sessionId: number, updates: Partial<LessonPlanSessionModel>) => void;
}

const LessonPlanSlideDeck: React.FC<LessonPlanSlideDeckProps> = ({ session, lessonPlanId, isExpanded = false, myRoles, isPublished = false, hasHumanCoach = false, updateSlide: updateSlideProp, updateSession: updateSessionProp }) => {
    const posthog = usePostHog();
    const { organization } = useAppContext();
    const { isEditMode } = useLessonPlanEditor();
    const { activeSlideId, setActiveSlideId, activeSessionId, isStarted, ...recordingContext } = useLessonPlanRecording();
    const [hasTrackedFirstSlide, setHasTrackedFirstSlide] = useState(false);
    const [voiceNavigationCommand, setVoiceNavigationCommand] = useState<VoiceNavigationCommand | null>(null);
    const [isVoiceFacilitatorActive, setIsVoiceFacilitatorActive] = useState(false);
    // Script Reader is the only mode offered for now; the conversational path is kept
    // below but unreachable until the mode picker comes back.
    const [facilitatorMode] = useState<FacilitatorMode>('scripted');
    const [slideListAnchor, setSlideListAnchor] = useState<HTMLElement | null>(null);
    const [isMarkingComplete, setIsMarkingComplete] = useState(false);
    const slideListOpen = Boolean(slideListAnchor);
    const isCompleted = !!session.completed_at;
    const allSlidesTracked = areAllSlidesTrackedForSession(session);
    const mayManageProgress = canManageSessionProgress(myRoles, hasHumanCoach);
    // Track which slides have been tracked by ID to avoid duplicate API calls
    const trackedSlidesRef = useRef<Set<number>>(new Set());
    const lastAppliedVoiceCommandIdRef = useRef<number | null>(null);
    const voiceNavigationCommandIdRef = useRef(0);

    // Slides are pre-ordered by the backend (slide_number); filter nulls and convert to display format
    const slides = (session.slides || [])
        .filter((slide): slide is SessionSlideModel => slide != null)
        .map(slide => ({
            ...slide,
            scripts: {
                open: slide.script_open,
                on_neutral: slide.script_on_neutral,
                on_negative: slide.script_on_negative,
                on_positive: slide.script_on_positive
            }
        }));
    const totalSlides = slides.length;
    const isActiveSession = session?.id === activeSessionId;
    const activeSlideIndex = isActiveSession
        ? Math.max(0, slides.findIndex((s) => s.id === activeSlideId))
        : 0;
    // Stable id for the active slide (used in effect deps to avoid re-running when `slides` array reference changes)
    const activeSlideIdForDeck = slides[activeSlideIndex]?.id;

    // Track the initial/current slide when session is expanded and not already tracked
    // Don't call onProgressUpdate on initial mount to avoid triggering refetches
    // Coach and parent can send update_slide_progress to the API
    const trackInitialSlide = useCallback(async () => {
        if (!isPublished || !mayManageProgress || !lessonPlanId || !session?.id) {
            return;
        }
        const slide = slides[activeSlideIndex];
        if (!slide?.id) {
            return;
        }
        if (trackedSlidesRef.current.has(slide.id)) {
            return;
        }

        try {
            const now = new Date().toISOString();
            const wasFirst = !hasTrackedFirstSlide;
            const sessionPayload: Record<string, unknown> = {
                slides: {
                    [String(slide.id)]: { started_at: now }
                }
            };
            if (wasFirst) {
                sessionPayload.started_at = now;
                setHasTrackedFirstSlide(true);
            }
            const progressUpdate = {
                sessions: {
                    [String(session.id)]: sessionPayload
                }
            };

            await ApiClient.patch(`/api/lesson-plan/${lessonPlanId}/update_slide_progress/`, progressUpdate);
            trackedSlidesRef.current.add(slide.id);
            // Update local state so Mark Complete becomes visible without refetch
            updateSlideProp?.(slide.id, { started_at: now });
            if (wasFirst) updateSessionProp?.(session.id, { started_at: now });
        } catch (error) {
            console.error('Error tracking slide progress:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPublished, mayManageProgress, lessonPlanId, session?.id, activeSlideIndex, slides, updateSlideProp, updateSessionProp]);

    useEffect(() => {
        trackedSlidesRef.current = new Set();
        setHasTrackedFirstSlide(false);
    }, [session?.id]);

    useEffect(() => {
        // Track the active slide on mount and whenever the deck lands on a new
        // slide without going through Next (session tab / first-unstarted jump).
        if (!isExpanded || !isActiveSession) return;
        void trackInitialSlide();
        // trackInitialSlide identity changes with `slides`; depend on primitives.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded, isActiveSession, activeSlideIdForDeck, session?.id, isPublished, lessonPlanId]);

    // PostHog: fire lesson_slide_viewed when user lands on a slide (all users)
    useEffect(() => {
        const slide = slides[activeSlideIndex];
        if (!posthog || !slide?.id || !lessonPlanId || !session?.id) return;
        const slideNumber = activeSlideIndex + 1;
        capture(posthog, "lesson_slide_viewed", {
            lesson_plan_id: lessonPlanId,
            session_id: session.id,
            session_number: session.session_number ?? session.id,
            slide_id: slide.id,
            slide_number: slideNumber,
            total_slides: totalSlides,
            is_first_slide: slideNumber === 1,
            is_last_slide: slideNumber === totalSlides,
            organization_id: organization?.id
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posthog, activeSlideIndex, lessonPlanId, session?.id, totalSlides, organization?.id]);

    // Automatic restart: only when user has manually started and this deck is the active session.
    // Recording always requires an initial manual Start; then we upload/restart between slides until Stop.
    // Dependencies use activeSlideIdForDeck (primitive) instead of slides to avoid re-running on every
    // render—slides is recreated each render and would otherwise cause start/stop recording loops.
    useEffect(() => {
        if (!lessonPlanId || !session?.id) return;
        if (!isStarted) return; // never auto-start; only restart after user has clicked Start
        if (!isActiveSession) return; // only the active session's deck

        const startSlideRecording = async () => {
            try {
                await recordingContext.startRecording(
                    lessonPlanId,
                    session.id,
                    activeSlideIdForDeck ?? undefined,
                    false // fromUser: restart on slide change, not manual
                );
            } catch (error: any) {
                console.error('Error starting automatic recording:', error);
            }
        };

        startSlideRecording();

        return () => {
            if (recordingContext.isRecording()) {
                recordingContext.stopRecording(/* fromUser: false */).catch((error: any) => {
                    console.error('Error stopping recording on cleanup:', error);
                });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonPlanId, session?.id, activeSlideIndex, activeSlideIdForDeck, isActiveSession, isStarted]);

    const trackSlideProgress = async (slideIndex: number, skipIfTracked: boolean = true, notifyParent: boolean = true) => {
        if (!isPublished || !mayManageProgress || !lessonPlanId || !session?.id) {
            return;
        }
        const slide = slides[slideIndex];
        if (!slide?.id) {
            return;
        }
        if (skipIfTracked && trackedSlidesRef.current.has(slide.id)) {
            return;
        }

        try {
            const now = new Date().toISOString();
            const wasFirst = !hasTrackedFirstSlide;
            const sessionPayload: Record<string, unknown> = {
                slides: {
                    [String(slide.id)]: { started_at: now }
                }
            };
            if (wasFirst) {
                sessionPayload.started_at = now;
                setHasTrackedFirstSlide(true);
            }
            const progressUpdate = {
                sessions: {
                    [String(session.id)]: sessionPayload
                }
            };

            await ApiClient.patch(`/api/lesson-plan/${lessonPlanId}/update_slide_progress/`, progressUpdate);
            trackedSlidesRef.current.add(slide.id);
            // Update local state so Mark Complete becomes visible without refetch
            updateSlideProp?.(slide.id, { started_at: now });
            if (wasFirst) updateSessionProp?.(session.id, { started_at: now });
        } catch (error) {
            console.error('Error tracking slide progress:', error);
        }
    };

    const fireSlideNavigated = (fromStep: number, toStep: number, direction: 'next' | 'previous' | 'dropdown') => {
        if (!posthog || !lessonPlanId || !session?.id) return;
        capture(posthog, "lesson_slide_navigated", {
            lesson_plan_id: lessonPlanId,
            session_id: session.id,
            session_number: session.session_number ?? session.id,
            from_slide_number: fromStep + 1,
            to_slide_number: toStep + 1,
            direction,
            organization_id: organization?.id
        });
    };

    const navigateToStep = useCallback(async (
        newStep: number,
        direction: 'next' | 'previous' | 'dropdown',
    ) => {
        if (newStep === activeSlideIndex || newStep < 0 || newStep >= totalSlides) return;
        fireSlideNavigated(activeSlideIndex, newStep, direction);
        if (recordingContext.isRecording()) {
            await recordingContext.stopRecording();
        }
        setActiveSlideId(slides[newStep]?.id ?? null);
        trackSlideProgress(newStep);
        // New recording will start automatically via useEffect
    }, [activeSlideIndex, totalSlides, recordingContext, setActiveSlideId, slides, trackSlideProgress]);

    useEffect(() => {
        const command = voiceNavigationCommand;
        if (!command) return;
        if (lastAppliedVoiceCommandIdRef.current === command.id) return;
        if (command.intent.sessionId !== session?.id) return;

        lastAppliedVoiceCommandIdRef.current = command.id;
        if (command.intent.direction === 'next') {
            void navigateToStep(Math.min(activeSlideIndex + 1, totalSlides - 1), 'next');
            return;
        }
        if (command.intent.direction === 'prev') {
            void navigateToStep(Math.max(activeSlideIndex - 1, 0), 'previous');
            return;
        }
        if (command.intent.direction === 'goto' && command.intent.slideId) {
            const targetIndex = slides.findIndex(slide => slide.id === command.intent.slideId);
            if (targetIndex >= 0) {
                void navigateToStep(targetIndex, 'dropdown');
            }
        }
    }, [voiceNavigationCommand, session?.id, activeSlideIndex, totalSlides, slides, navigateToStep]);

    const handleVoiceNavigationIntent = useCallback((intent: VoiceNavigationIntent) => {
        if (!session?.id || intent.sessionId !== session.id) return;
        voiceNavigationCommandIdRef.current += 1;
        setVoiceNavigationCommand({
            id: voiceNavigationCommandIdRef.current,
            intent,
        });
    }, [session?.id]);

    const handleVoiceActiveChange = useCallback((voiceActive: boolean) => {
        setIsVoiceFacilitatorActive(voiceActive);
    }, []);

    const handleSlideSelect = async (newStep: number) => {
        setSlideListAnchor(null);
        await navigateToStep(newStep, 'dropdown');
    };

    const handleNext = async () => {
        const newStep = Math.min(activeSlideIndex + 1, totalSlides - 1);
        await navigateToStep(newStep, 'next');
    };

    const handleBack = async () => {
        const newStep = Math.max(activeSlideIndex - 1, 0);
        await navigateToStep(newStep, 'previous');
    };

    const handleMarkComplete = async () => {
        if (!session?.id || isMarkingComplete || !isPublished) {
            return;
        }
        capture(posthog, "lesson_session_mark_complete_clicked", {
            lesson_plan_id: lessonPlanId != null && typeof lessonPlanId === 'number' ? lessonPlanId : undefined,
            session_id: session.id,
            session_number: session.session_number,
            organization_id: organization?.id
        });
        setIsMarkingComplete(true);
        try {
            await ApiClient.post(`/api/lesson-plan-sessions/${session.id}/mark_complete/`, {});
            updateSessionProp?.(session.id, { completed_at: new Date().toISOString() });
            if (lessonPlanId != null && typeof lessonPlanId === 'number') {
                capture(posthog, "lesson_session_completed", {
                    lesson_plan_id: lessonPlanId,
                    session_id: session.id,
                    session_number: session.session_number,
                    organization_id: organization?.id
                });
            }
        } catch (error) {
            console.error('Error marking session as complete:', error);
        } finally {
            setIsMarkingComplete(false);
        }
    };

    if (totalSlides === 0) {
        return (
            <Box sx={{ mt: 3, p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                    No slides available in this lesson plan.
                </Typography>
            </Box>
        );
    }

    const currentSlide = slides[activeSlideIndex];
    const sessionNumber = session.session_number ?? 1;
    const showPriorWeekCheckIn = (myRoles?.includes('coach') || myRoles?.includes('parent')) && (sessionNumber >= 2 && activeSlideIndex === 0);

    const handleDeckKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const target = event.target;
        if (
            target instanceof HTMLInputElement ||
            target instanceof HTMLTextAreaElement ||
            target instanceof HTMLButtonElement
        ) {
            return;
        }
        if (event.key === 'ArrowLeft' && activeSlideIndex > 0) {
            event.preventDefault();
            void handleBack();
        } else if (event.key === 'ArrowRight' && activeSlideIndex < totalSlides - 1) {
            event.preventDefault();
            void handleNext();
        }
    };

    return (
        <Box
            data-testid={`lesson-plan-slide-deck--session-${session.session_number ?? session.id}`}
            tabIndex={0}
            onKeyDown={handleDeckKeyDown}
            aria-label="Lesson slides. Use left and right arrow keys to change slides."
            sx={{ '&:focus-visible': { outline: 'none' } }}
        >

            {/* Slide Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>

                {mayManageProgress && isPublished && allSlidesTracked ? (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleMarkComplete}
                            disabled={isCompleted || isMarkingComplete || !allSlidesTracked}
                            startIcon={isMarkingComplete ? <CircularProgress size={16} /> : allSlidesTracked ? <TaskAltIcon fontSize="small" /> : null}
                            title={!allSlidesTracked ? 'Complete all slides before marking as complete' : undefined}
                            data-attr="lesson-plan-card_mark-complete"
                            data-custom-id={String(session.id)}
                        >
                            {isMarkingComplete ? 'Marking...' : isCompleted ? <span data-testid="lesson-plan-card-completed">Completed</span> : allSlidesTracked ? 'Mark Complete' : ''}
                        </Button>
                    </Box>
                ) : isCompleted ? (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={isCompleted}
                        startIcon={<TaskAltIcon fontSize="small" />}
                        data-attr="lesson-plan-card_mark-complete"
                        data-custom-id={String(session.id)}
                    >
                        <span data-testid="lesson-plan-card-completed">Completed</span>
                    </Button>
                ) : null}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!isVoiceFacilitatorActive && (
                        <>
                            <Button
                                onClick={(event) => setSlideListAnchor(event.currentTarget)}
                                endIcon={<ArrowDropDownIcon />}
                                variant="outlined"
                                size="small"
                                aria-label={`Slide ${activeSlideIndex + 1} of ${totalSlides}. View slide list`}
                                aria-haspopup="menu"
                                aria-expanded={slideListOpen}
                                data-attr="lesson-plan-slide-deck_slide-select"
                                data-testid="lesson-plan-slide-deck_slide-select"
                            >
                                View Slide List
                            </Button>
                            <Popover
                                open={slideListOpen}
                                anchorEl={slideListAnchor}
                                onClose={() => setSlideListAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                slotProps={{
                                    paper: {
                                        sx: { minWidth: 280, maxWidth: 420, maxHeight: 360 },
                                    },
                                }}
                            >
                                <MenuList
                                    dense
                                    disablePadding
                                    aria-label="Slides"
                                    data-testid="lesson-plan-slide-deck_slide-select-popover"
                                    data-attr="lesson-plan-slide-deck_slide-select-popover"
                                >
                                    {slides.map((slide, index) => (
                                        <MenuItem
                                            key={slide.id ?? index}
                                            selected={index === activeSlideIndex}
                                            onClick={() => void handleSlideSelect(index)}
                                            data-testid={`lesson-plan-slide-deck_slide-select-option--${index + 1}`}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                {slide.started_at && (
                                                    <TaskAltIcon color="success" fontSize="small" titleAccess="Slide tracked" />
                                                )}
                                                <Typography component="span" noWrap>
                                                    {slide.title || `Slide ${index + 1}`}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </Popover>
                            <ButtonGroup variant="outlined" aria-label="slide navigation">
                                <Button
                                    onClick={handleBack}
                                    disabled={activeSlideIndex === 0}
                                    startIcon={<ArrowBackIcon />}
                                    size="small"
                                    aria-label="Previous slide"
                                    data-attr="lesson-plan-slide-deck_previous"
                                >
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                        Previous slide
                                    </Box>
                                </Button>
                                <Button
                                    onClick={handleNext}
                                    disabled={activeSlideIndex === totalSlides - 1}
                                    endIcon={<ArrowForwardIcon />}
                                    size="small"
                                    variant="outlined"
                                    aria-label="Next slide"
                                    data-attr="lesson-plan-slide-deck_next"
                                >
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                        Next slide
                                    </Box>
                                </Button>
                            </ButtonGroup>
                        </>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                    {lessonPlanId && isPublished && (
                        isStarted ? (
                            <Button
                                onClick={() => recordingContext.stopRecording(true).catch((e) => console.error('Stop session recording:', e))}
                                variant="outlined"
                                size="small"
                                startIcon={<StopIcon />}
                                color="error"
                                data-attr="lesson-plan-slide-deck_stop-recording"
                            >
                                Stop Recording
                            </Button>
                        ) : (
                            <Button
                                onClick={() => recordingContext.startRecording(lessonPlanId!, activeSessionId ?? undefined, activeSlideId ?? undefined, true).catch((e) => console.error('Start session recording:', e))}
                                variant="outlined"
                                size="small"
                                startIcon={<PlayArrowIcon />}
                                data-attr="lesson-plan-slide-deck_start-recording"
                            >
                                Start Recording
                            </Button>
                        )
                    )}

                    {lessonPlanId && isPublished && myRoles?.includes('coach') && (
                        <Button
                            component={RouterLink}
                            to={`/recordings?lesson_plan_id=${lessonPlanId}${session?.id ? `&session_id=${session.id}` : ''}`}
                            variant="outlined"
                            size="small"
                            data-attr="lesson-plan-slide-deck_view-recordings"
                        >
                            View Recordings
                        </Button>
                    )}

                </Box>
            </Box>

            {!isEditMode && (
                <>
                    {/* Mode is locked while a run is live — switching would swap the
                        component out from under an open Voice Live socket. */}
                    {/* <ButtonGroup size="small" sx={{ mt: 1, mb: 1 }} disabled={isVoiceFacilitatorActive}>
                        <Button
                            variant={facilitatorMode === 'conversational' ? 'contained' : 'outlined'}
                            onClick={() => setFacilitatorMode('conversational')}
                            data-attr="lesson-plan-slide-deck_mode-conversational"
                        >
                            Conversational
                        </Button>
                        <Button
                            variant={facilitatorMode === 'scripted' ? 'contained' : 'outlined'}
                            onClick={() => setFacilitatorMode('scripted')}
                            data-attr="lesson-plan-slide-deck_mode-scripted"
                        >
                            Script Reader
                        </Button>
                    </ButtonGroup> */}

                    {facilitatorMode === 'scripted' ? (
                        <LessonPlanScriptedSession
                            lessonPlanId={lessonPlanId}
                            session={session}
                            isPublished={isPublished}
                            updateSlide={updateSlideProp}
                            updateSession={updateSessionProp}
                            onNavigationIntent={handleVoiceNavigationIntent}
                            onVoiceActiveChange={handleVoiceActiveChange}
                        />
                    ) : (
                        <LessonPlanFacilitatorSession
                            lessonPlanId={lessonPlanId}
                            session={session}
                            isPublished={isPublished}
                            updateSlide={updateSlideProp}
                            updateSession={updateSessionProp}
                            onNavigationIntent={handleVoiceNavigationIntent}
                            onVoiceActiveChange={handleVoiceActiveChange}
                        />
                    )}
                </>
            )}

            {/* Current Slide */}
            {currentSlide && (
                <LessonPlanSlide
                    slide={currentSlide}
                    slideNumber={activeSlideIndex + 1}
                    totalSlides={totalSlides}
                    myRoles={myRoles}
                    showPriorWeekCheckIn={showPriorWeekCheckIn}
                />
            )}
        </Box>
    );
};

export default LessonPlanSlideDeck;

